import React, { useState } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Avatar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  useTheme
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AdminPanelSettings as AdminIcon,
  Person as CustomerIcon,
  CleaningServices as CleanerIcon,
  MoreVert as MoreIcon,
} from "@mui/icons-material";

const mockRoles = [
  {
    id: 1,
    name: "Administrator",
    description: "Full system access with all permissions",
    userCount: 3,
    permissions: ["users:read", "users:write", "settings:read", "settings:write", "reports:read", "reports:write"],
    color: "error"
  },
  {
    id: 2,
    name: "Cleaner",
    description: "Cleaner access with job management",
    userCount: 156,
    permissions: ["jobs:read", "jobs:write", "schedule:read", "schedule:write"],
    color: "primary"
  },
  {
    id: 3,
    name: "Customer",
    description: "Customer access for booking services",
    userCount: 1024,
    permissions: ["bookings:read", "bookings:write", "profile:read", "profile:write"],
    color: "secondary"
  },
  {
    id: 4,
    name: "Manager",
    description: "Management access for team oversight",
    userCount: 8,
    permissions: ["users:read", "reports:read", "schedule:read", "schedule:write"],
    color: "warning"
  }
];

const allPermissions = [
  "users:read",
  "users:write",
  "settings:read",
  "settings:write",
  "reports:read",
  "reports:write",
  "jobs:read",
  "jobs:write",
  "schedule:read",
  "schedule:write",
  "bookings:read",
  "bookings:write",
  "profile:read",
  "profile:write",
  "billing:read",
  "billing:write"
];

export default function UserRoles() {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [newRole, setNewRole] = useState({
    name: "",
    description: "",
    permissions: []
  });

  const filteredRoles = mockRoles.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleIcon = (roleName) => {
    switch (roleName) {
      case 'Administrator': return <AdminIcon />;
      case 'Cleaner': return <CleanerIcon />;
      case 'Customer': return <CustomerIcon />;
      default: return <AdminIcon />;
    }
  };

  const handleOpenDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setNewRole({
        name: role.name,
        description: role.description,
        permissions: role.permissions
      });
    } else {
      setEditingRole(null);
      setNewRole({
        name: "",
        description: "",
        permissions: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRole(null);
    setNewRole({
      name: "",
      description: "",
      permissions: []
    });
  };

  const handleSaveRole = () => {
    // Here you would save the role to your backend
    console.log('Saving role:', newRole);
    handleCloseDialog();
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            User Roles & Permissions
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Role
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage user roles and their system permissions
        </Typography>
      </Box>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search roles..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Roles Grid */}
      <Grid container spacing={3}>
        {filteredRoles.map((role) => (
          <Grid item xs={12} md={6} key={role.id}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ bgcolor: `${role.color}.main` }}>
                      {getRoleIcon(role.name)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="600">
                        {role.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {role.userCount} users
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={() => handleOpenDialog(role)}>
                    <EditIcon />
                  </IconButton>
                </Box>

                <Typography variant="body2" sx={{ mb: 2 }}>
                  {role.description}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    Permissions:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {role.permissions.map((permission, index) => (
                      <Chip
                        key={index}
                        label={permission}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    ))}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Switch defaultChecked color="success" />
                  <Typography variant="body2" color="text.secondary">
                    Active
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add/Edit Role Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRole ? 'Edit Role' : 'Add New Role'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Role Name"
              value={newRole.name}
              onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description"
              value={newRole.description}
              onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Permissions</InputLabel>
              <Select
                multiple
                value={newRole.permissions}
                onChange={(e) => setNewRole({ ...newRole, permissions: e.target.value })}
                renderValue={(selected) => selected.join(', ')}
                label="Permissions"
              >
                {allPermissions.map((permission) => (
                  <MenuItem key={permission} value={permission}>
                    <Checkbox checked={newRole.permissions.indexOf(permission) > -1} />
                    <ListItemText primary={permission} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveRole} variant="contained">
            {editingRole ? 'Update Role' : 'Create Role'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}